import {computed, inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {catchError, finalize, map, Observable, throwError, timeout} from 'rxjs';
import {
  BreakIn,
  BreakOut,
  ClientSearchResp,
  ClockInOutResponse,
  Events,
  FichaInOutPayload,
  ProneFirstLoad,
} from '../interfaces/schedule.interface';
import { CheckInOutResponse, EventsPayloadV2, EventsResponseV2, ReasonCatalogResponse } from '../interfaces/scheadule-v2.interface';
import { LoadingService } from '@common/services/loading.service';
import { ApiRouteService } from '@common/api';
import { SessionContextService } from '@common/api/session-context.service';
import { MotivoIncidencia, MotivoTypes, ReasonCode } from '@fichaje/interfaces/motivo.interface';
import { AjusteJornadaSinFichar, OvertimeInPayload, OvertimeOutPayload, RemoteWorkPayload } from '@fichaje/interfaces/http-payload.interface';
import { PreloadConfigService } from '@common/config/preload-config.service';
import { BackgroundImagesResp } from "@fichaje/interfaces/ficha.interface";
import { ApiVersion } from "@common/interfaces/api-version.interface";
import {
  ActionEventsRefreshPayload,
  adaptHybridActionEventsEnvelope,
  adaptHybridActionEventsPayload,
} from '../adapters/action-events-compat.adapter';

type ActionEventsResponse = CheckInOutResponse | ClockInOutResponse | Events | EventsResponseV2;
type ClientSearchResponseV2 = {
  status: string;
  message: string;
  data: ClientSearchResp[];
};
type OptionalScheduleSelectPayload = {
  fecha: string;
  horario_id: number;
};
type OptionalScheduleReleasePayload = {
  fecha: string;
};
type OptionalScheduleMutationResponse = {
  status: string;
  message: string;
  data: unknown;
  errors: unknown;
};


@Injectable({
  providedIn: 'root'
})
export class HttpFichaService {
  // Dependency injections

  private readonly KIOSK_REQUEST_TIMEOUT_MS = 90000;
  // Mismo formato que valida CalendarController::getEventsByWorkerByDateWithUserLogged
  // en el backend (parámetro `dni`, separado de `codigo`): 8 dígitos + letra, o NIE
  // (X/Y/Z + 7 dígitos) + letra.
  private readonly dniRegex = /^(?:\d{8}|[XYZxyz]\d{7})[A-Za-z]$/;
  version:ApiVersion = 'v2';
  private readonly preConfServ = inject(PreloadConfigService);
  preUrl = computed(() => this.preConfServ.config()?.apiUrl);
  preProneUrl = computed(() => this.preConfServ.config()?.apiProneUrl);

  /*
    private readonly URL =      environment.apiUrl;
    private readonly ProneURL = environment.apiProneUrl;
    preUrl = computed( () => this.URL )
    preProneUrl = computed( () => this.ProneURL )
  */

  private readonly http = inject(HttpClient);
  private readonly loading = inject(LoadingService)
  private readonly apiRoute = inject(ApiRouteService);
  private readonly sessionContext = inject(SessionContextService);
  /*private readonly help = inject(HelperService)  */


// * ################################################################################################
// * ####################################   NUEVAS FUNCIONES   ######################################
// * ################################################################################################

//* Permisos
permissionInOut(inOut: 'in' | 'out', scheduleId: number, dataPayload: FichaInOutPayload): Observable<ActionEventsRefreshPayload> {
  try {
    const route = this.apiRoute.permissionInOut(
      inOut,
      scheduleId,
      !!dataPayload.isStation,
      dataPayload.company_id,
    );
    const workerId = route.context.workerId;


    if (workerId === undefined || workerId === null) {
      return throwError(() => new Error('Worker id not available for permissionInOut'));
    }

    this.validateContextConsistency({
      isStation: route.context.device === 'kiosk',
      companyId: route.context.companyId,
      operation: 'permissionInOut',
    });

    const payloadWithContext: FichaInOutPayload = {
      ...dataPayload,
      company_id: route.context.companyId,
      worker_id: workerId,
      isStation: route.context.device === 'kiosk',
    };

    return this.withKioskTimeout(
      this.http.post<ActionEventsResponse>(route.path, payloadWithContext).pipe(
        map((response) => this.normalizeActionResponseToEvents(response)),
        catchError((err) => throwError(() => err))
      ),
      route.context.device === 'kiosk'
    );
  } catch (error) {
    return throwError(() => error);
  }
}
//* BreakInOut
breakInOut(inOut: 'in' | 'out', _scheduleId: number, dataPayload: BreakIn | BreakOut): Observable<ActionEventsRefreshPayload> {
  try {
    const route = this.apiRoute.breakInOut(
      inOut,
      !!dataPayload.isStation,
      dataPayload.company_id,
    );
    const workerId = route.context.workerId;


    if (workerId === undefined || workerId === null) {
      return throwError(() => new Error('Worker id not available for breakInOut'));
    }

    this.validateContextConsistency({
      isStation: route.context.device === 'kiosk',
      companyId: route.context.companyId,
      operation: 'breakInOut',
    });

    const payloadWithContext = {
      ...dataPayload,
      company_id: route.context.companyId,
      worker_id: workerId,
      isStation: route.context.device === 'kiosk',
    };

    return this.withKioskTimeout(
      this.http.post<ActionEventsResponse>(route.path, payloadWithContext).pipe(
        map((response) => this.normalizeActionResponseToEvents(response)),
        catchError((err) => throwError(() => err))
      ),
      route.context.device === 'kiosk'
    );
  } catch (error) {
    return throwError(() => error);
  }
}

// * ################################################################################################

  getEvents(companyId: number, date: string, type: string, worker_id: number | undefined, isStation: boolean): Observable<EventsPayloadV2> { //! IS STATION PENDIENTE
    this.validateContextConsistency({ isStation, companyId, operation: 'getEvents' });
    const route = this.apiRoute.canonicalEvents(isStation, companyId);
    const resolvedWorkerId = worker_id ?? this.sessionContext.getWorkerId();

    if (resolvedWorkerId === undefined || resolvedWorkerId === null) {
      console.error('[HttpFichaService] Missing worker_id for getEvents', {
        companyId,
        date,
        type,
        isStation,
      });
      return throwError(() => new Error('[HttpFichaService] Missing worker_id for getEvents'));
    }

    this.loading.startEventLoading();
    let params = new HttpParams()
      .set('date', date)
      .set('type', type)
      .set('worker_id', String(resolvedWorkerId));

    return this.withKioskTimeout(
      this.http.get<EventsResponseV2>(route.path, {params}).pipe(
        map(r => r.data),
        finalize(() => this.loading.stopEventLoading()),
      ),
      isStation
    )
  }
  ajusteNoFichado(isStation: boolean, payload: AjusteJornadaSinFichar): Observable<ActionEventsRefreshPayload> {
    const companyId = payload.company_id;
    const apiURL = this.getBackendUrl(isStation);

    const url = `${apiURL}ajuste/${companyId}/jornada/pre-set`
    return this.withKioskTimeout(
      this.http.post<ActionEventsResponse>(url, payload).pipe(
        map((response) => this.normalizeActionResponseToEvents(response)),
        catchError((err) => throwError(() => err))
      ),
      isStation
    )

  }


  getReason(companyId: number, reasonType: ReasonCode, isStation: boolean): Observable<MotivoIncidencia[]> {
    this.validateContextConsistency({ isStation, companyId, operation: 'getReason' });
    this.loading.startMotivoLoading();
    const route = this.apiRoute.reasons(reasonType, isStation, companyId);

    return this.withKioskTimeout(
      this.http.get<ReasonCatalogResponse>(route.path).pipe(
        map((response) => response.data as MotivoIncidencia[]),
        finalize(() => this.loading.stopMotivoLoading())
      ),
      isStation
    )
  }

  getMotivosIncidencia(
    companyId: number,
    motivoType: MotivoTypes,
    isStation: boolean,
    inOut: 'in' | 'out' | undefined = undefined,
  ): Observable<MotivoIncidencia[]> {
    return this.getReason(companyId, this.mapMotivoTypeToReasonCode(motivoType, inOut), isStation);
  }

  // https://angel.control-documental.com/api/v2/calendar/4397167/in
  // api/v2/
  checkInOut(checkType: 'check-in' | 'check-out', scheduleId: number, dataPayload: FichaInOutPayload): Observable<ActionEventsRefreshPayload> {
    try {
      const isStation = !!dataPayload.isStation;
      const route = this.apiRoute.checkInOut(checkType, scheduleId, isStation, dataPayload.company_id);
      // En el kiosk el trabajador es el del evento, no el `user` de localStorage (que no existe o es de la web).
      const workerId = isStation ? dataPayload.worker_id : route.context.workerId;

      if (workerId === undefined || workerId === null) {
        return throwError(() => new Error('Worker id not available for checkInOut'));
      }

      this.validateContextConsistency({
        isStation: route.context.device === 'kiosk',
        companyId: route.context.companyId,
        operation: 'checkInOut',
      });

      const payload: FichaInOutPayload = {
        ...dataPayload,
        company_id: route.context.companyId,
        worker_id: workerId,
        isStation: route.context.device === 'kiosk',
      };

      return this.withKioskTimeout(
        this.http.post<ActionEventsResponse>(route.path, payload).pipe(
          // Se unifica la forma de respuesta para evitar que la UI maneje múltiples contratos.
          map((response) => this.normalizeActionResponseToEvents(response)),
          catchError((err) => throwError(() => err))
        ),
        route.context.device === 'kiosk'
      )
    } catch (error) {
      return throwError(() => error);
    }
  }

  ficharInOut(inOut: 'in' | 'out', scheduleId: number, dataPayload: FichaInOutPayload): Observable<ActionEventsRefreshPayload> {
    const checkType = inOut === 'in' ? 'check-in' : 'check-out';
    return this.checkInOut(checkType, scheduleId, dataPayload);
  }

  // Normaliza respuestas de acciones para aceptar tanto `Events` directo como `ClockInOutResponse` anidado.
  // Esto evita fallos intermitentes cuando el backend responde con contratos distintos según contexto.
  private normalizeActionResponseToEvents(response: CheckInOutResponse | ClockInOutResponse | Events | EventsResponseV2 | undefined): ActionEventsRefreshPayload {
    if (!response) return this.emptyRefreshPayload();

    if (this.isEventsShape(response)) {
      return { legacyEvents: response };
    }

    if ('events' in response && this.isEventsShape(response.events)) {
      return { legacyEvents: response.events };
    }

    if ('data' in response && response.data && this.hasNestedEventsEnvelope(response.data)) {
      const adapted = adaptHybridActionEventsEnvelope(response.data.events);
      if (adapted) return adapted;
    }

    if ('data' in response && response.data && this.hasNestedLegacyEvents(response.data)) {
      return { legacyEvents: response.data.events };
    }

    if ('data' in response && response.data && this.isEventsPayloadV2Shape(response.data)) {
      return {
        canonicalEvents: response.data,
        legacyEvents: adaptHybridActionEventsPayload(response.data),
      };
    }

    return this.emptyRefreshPayload();
  }

  private isEventsShape(value: unknown): value is Events {
    if (!value || typeof value !== 'object') return false;
    const candidate = value as Partial<Events>;
    return !!candidate.meta && Array.isArray(candidate.data);
  }

  private isEventsPayloadV2Shape(value: unknown): value is EventsPayloadV2 {
    if (!value || typeof value !== 'object') return false;
    const candidate = value as Partial<EventsPayloadV2>;
    return !!candidate.meta && Array.isArray(candidate.data);
  }

  private hasNestedLegacyEvents(value: unknown): value is { events: Events } {
    if (!value || typeof value !== 'object') return false;
    const candidate = value as { events?: unknown };
    return this.isEventsShape(candidate.events);
  }

  private hasNestedEventsEnvelope(value: unknown): value is { events: unknown } {
    if (!value || typeof value !== 'object') return false;
    return 'events' in value;
  }

  // Fallback defensivo para mantener el pipeline de signals estable ante respuestas incompletas.
  private emptyRefreshPayload(): ActionEventsRefreshPayload {
    return { legacyEvents: { meta: {} as Events['meta'], data: [] } };
  }

  timeOffInOut(inOut: 'in' | 'out', scheduleId: number, dataPayload: FichaInOutPayload): Observable<ActionEventsRefreshPayload> {

    const payload: FichaInOutPayload = dataPayload;
    const isStation = !!payload.isStation;

    if (!isStation) {
      return this.permissionInOut(inOut, scheduleId, payload);
    }

    this.validateContextConsistency({ isStation, companyId: payload.company_id, operation: 'timeOffInOut(kiosk-legacy)' });
    const apiURL = this.getBackendUrl(isStation);
    if (!(scheduleId || payload.company_id || payload.worker_id))
      console.error('Payload incomplete')

    const url = `${apiURL}request/others/${scheduleId}/${inOut}`
    return this.withKioskTimeout(
      this.http.post<ActionEventsResponse>(url, payload).pipe(
        map((response) => this.normalizeActionResponseToEvents(response)),
        catchError((err) => throwError(() => err))
      ),
      isStation
    )
  }


  timeOffInOutFullday(dataPayload: FichaInOutPayload & { observaciones?: string }): Observable<ActionEventsRefreshPayload> {
    const isStation = !!dataPayload.isStation;
    // worker_id: en el kiosk el middleware inyectar_worker resuelve el trabajador desde el body.
    // isStation: sin él, el backend sigue la rama web y necesita un usuario autenticado.
    const payload = {
      company_id: dataPayload.company_id,
      worker_id: dataPayload.worker_id,
      motivo_id: dataPayload.motivo_id,
      observaciones: dataPayload.observaciones ?? dataPayload.motivoIncidencia,
      isStation,
    };

    const apiURL = this.getBackendUrl(isStation);

    if (!(dataPayload.company_id || dataPayload.worker_id))
      console.error('Payload incomplete')

    const url = `${apiURL}request/others/${dataPayload.worker_id}/complete`
    return this.withKioskTimeout(
      this.http.post<ActionEventsResponse>(url, payload).pipe(
        map((response) => this.normalizeActionResponseToEvents(response)),
        catchError((err) => throwError(() => err))
      ),
      isStation
    )
  }

  overtimeInOut(inOut: 'in' | 'out', dataPayload: OvertimeInPayload | OvertimeOutPayload): Observable<ActionEventsRefreshPayload> {
    try {
      const route = this.apiRoute.extraHoursInOut(
        inOut,
        !!dataPayload.isStation,
        dataPayload.company_id,
      );
      const workerId = route.context.workerId;

      if (workerId === undefined || workerId === null) {
        return throwError(() => new Error('Worker id not available for overtimeInOut'));
      }

      this.validateContextConsistency({
        isStation: route.context.device === 'kiosk',
        companyId: route.context.companyId,
        operation: 'overtimeInOut',
      });

      const payload = {
        ...dataPayload,
        company_id: route.context.companyId,
        worker_id: workerId,
        isStation: route.context.device === 'kiosk',
        ...(inOut === 'out' ? { shedule_id: dataPayload.schedule_id } : {}),
      };

      return this.withKioskTimeout(
        this.http.post<ActionEventsResponse>(route.path, payload).pipe(
          map((response) => this.normalizeActionResponseToEvents(response)),
          catchError((err) => throwError(() => err))
        ),
        route.context.device === 'kiosk'
      );
    } catch (error) {
      return throwError(() => error);
    }
  }

  remoteWork(payload: RemoteWorkPayload) {
    const {shedule_id, ...payloadClean} = payload;
    if (!(payload.shedule_id || payload.company_id || payload.value))
      console.error('Payload incomplete');

    const route = this.apiRoute.shiftRemoteWork(shedule_id);
    const payloadWithContext: Omit<RemoteWorkPayload, 'shedule_id'> = {
      ...payloadClean,
      company_id: route.context.companyId,
    };

    return this.http.post<void>(route.path, payloadWithContext).pipe(
      map(() => payloadWithContext.value),
      catchError((err) => throwError(() => err))
    )
  }

  getProneFirstLoad(inputCode: string, date: string): Observable<ProneFirstLoad> {
    // console.log('httpService - getProneUserEvents - date|inputCode: ', {date, inputCode} )

    this.loading.startEventLoading();
    const apiURL = this.getBackendUrl(true);
    const url = `${apiURL}worker/events`
    let params = new HttpParams()
      .set('date', date)
      .set('type', 'work,others,it,holidays,dld,libre');

    if (inputCode.length === 10) {
      params = params.append('tarjeta', inputCode);
    } else if (this.dniRegex.test(inputCode)) {
      params = params.append('dni', inputCode);
    } else {
      params = params.append('codigo', inputCode)
    }

    return this.withKioskTimeout(
      this.http.get<ProneFirstLoad>(url, {params}).pipe(
        finalize(() => this.loading.stopEventLoading()),
      ),
      true
    );
  }

  getBackgroundImages(token:string, isStation:boolean): Observable<BackgroundImagesResp> {
    const apiURL = this.getBackendUrl(isStation);
    let url = `${apiURL}fondos?token=${token}`;
    return this.withKioskTimeout(
      this.http.get<BackgroundImagesResp>(url).pipe(
        // finalize(() => this.loading.stopMotivoLoading())
      ),
      isStation
    )
  }

  clientsSearch(companyId:number, query:string, isStation:boolean): Observable<ClientSearchResp[]> {
    const route = this.apiRoute.customersSearch(isStation, companyId);
    const params = new HttpParams().set('query', query.trim());
    return this.withKioskTimeout(
      this.http.get<ClientSearchResponseV2>(route.path, { params }).pipe(
        map((response) => response.data ?? [])
      ),
      isStation
    )
  }

  clientSelect(companyId:number, clientId:number, scheduleId:number, isStation:boolean): Observable<void> {
    const route = this.apiRoute.shiftCustomer(scheduleId, isStation, companyId);
    const payload = {
      company_id: route.context.companyId,
      cliente_id: clientId
    }
    return this.withKioskTimeout(
      this.http.post<void>(route.path, payload)
      .pipe(
        // map((response) => this.normalizeActionResponseToEvents(response)),
        catchError((err) => throwError(() => err))
      ),
      isStation
    )
  }

  selectOptionalSchedule(
    companyId: number,
    workerId: number,
    fecha: string,
    horarioId: number,
    isStation = false,
  ): Observable<OptionalScheduleMutationResponse> {
    const route = this.apiRoute.optionalScheduleSelect(workerId, companyId, isStation);
    const payload: OptionalScheduleSelectPayload = {
      fecha,
      horario_id: horarioId,
    };

    return this.withKioskTimeout(
      this.http.post<OptionalScheduleMutationResponse>(route.path, payload).pipe(
        catchError((err) => throwError(() => err))
      ),
      isStation
    );
  }

  releaseOptionalSchedule(
    companyId: number,
    workerId: number,
    fecha: string,
    isStation = false,
  ): Observable<OptionalScheduleMutationResponse> {
    const route = this.apiRoute.optionalScheduleRelease(workerId, companyId, isStation);
    const payload: OptionalScheduleReleasePayload = { fecha };

    return this.withKioskTimeout(
      this.http.post<OptionalScheduleMutationResponse>(route.path, payload).pipe(
        catchError((err) => throwError(() => err))
      ),
      isStation
    );
  }

  private validateContextConsistency(input: {
    isStation: boolean;
    companyId?: number;
    operation: string;
  }): void {
    const inferredMode = this.sessionContext.mode();
    const modeConfidence = this.sessionContext.modeConfidence();
    const inferredCompanyId = this.sessionContext.getCompanyId(inferredMode);
    const expectedMode = input.isStation ? 'kiosk' : 'web';

    if (expectedMode !== inferredMode) {
      console.warn('[HttpFichaService] Mode mismatch detected', {
        operation: input.operation,
        expectedMode,
        inferredMode,
        modeConfidence,
      });
    }

    if (input.isStation && !this.sessionContext.isKioskConfirmed()) {
      console.warn('[HttpFichaService] Station request without confirmed kiosk token', {
        operation: input.operation,
        modeConfidence,
      });
    }

    if (
      input.companyId !== undefined
      && inferredCompanyId !== undefined
      && input.companyId !== inferredCompanyId
    ) {
      console.warn('[HttpFichaService] Company id mismatch detected', {
        operation: input.operation,
        inputCompanyId: input.companyId,
        inferredCompanyId,
      });
    }
  }

  getBackendUrl(isStation: boolean): string {
    let url = `${this.preUrl()!}${this.version}/`;

    if (isStation) url = `${this.preProneUrl()!}${this.version}/`;

    return url;
  }

  /**
   * Wraps an observable with a kiosk timeout if isStation is true.
   * Timeout errors propagate to the errorInterceptor which handles network errors.
   */
  private withKioskTimeout<T>(source: Observable<T>, isStation: boolean): Observable<T> {
    if (!isStation) return source;
    return source.pipe(timeout(this.KIOSK_REQUEST_TIMEOUT_MS));
  }

  private mapMotivoTypeToReasonCode(motivoType: MotivoTypes, inOut?: 'in' | 'out'): ReasonCode {
    switch (motivoType) {
      case 'horasextras':
        return 'extra-hours';
      case 'pausas':
        return 'breaks';
      case 'incidencia':
        if (inOut === 'in') return 'incident-in';
        if (inOut === 'out') return 'incident-out';
        return 'incidents';
      case 'gastos':
        return 'expenses';
      case 'others':
      case 'vacaciones':
      case 'ld':
      case 'it':
      case 'cambioHorario':
      case 'ajusteJornada':
      case 'noLaboral':
      default:
        return 'permissions';
    }
  }
}


// * ################################################################################################
// * ###############################   ENDPOINTS Y EQUIVALENTES   ###################################
// * ################################################################################################


//* get MOTIVOS
// /api/v2/1/reasons/permissions


//* post PERMISOS
// /api/v2/1/permissions/4396208/check-in
// /api/v2/1/permissions/4396208/check-out


//* get AJUSTE DE JORNADA
// /api/v2/ajuste/1/jornada/pre-set


//* get MAPA
// /api/v2/1/fichaje-jornada/calendar/map?lat=40.3737&lng=-3.6436&zoom=16&cache=1760976202


//* get EVENTS
// /api/v2/1/calendar/events/canonical?date=2026-05-19&type=work,others,it,holidays,dld,libre&worker_id=3541


//* post TELETRABAJO
// /api/v2/1/4397168/remote-work


//* post CLIENT SEARCH - Not working
// /api/v2/customers/1/search?query=pre


//* post PAUSAS
// /api/v2/pausas/1/fichar/in

